import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { IP_FAQ_CATEGORIES } from "@/content/ip-corpus/faq-defs";

const ADVICE_TH = [
  "ตรวจค้นฐานข้อมูลเครื่องหมายการค้าของกรมทรัพย์สินทางปัญญาก่อนยื่นทุกครั้ง รวมถึงคำพ้องเสียงและจำพวกที่เกี่ยวเนื่อง",
  "ยื่นคำขอสิทธิบัตรหรืออนุสิทธิบัตรก่อนนำเสนอผลงานต่อสาธารณะ เพื่อรักษาเงื่อนไขความใหม่",
  "ระบุรายการสินค้าและบริการเท่าที่ใช้จริง เพื่อลดความเสี่ยงถูกคัดค้านและถูกเพิกถอนเพราะไม่ได้ใช้",
  "เก็บหลักฐานการใช้เครื่องหมายที่ระบุวันที่อย่างต่อเนื่อง เช่น ใบกำกับภาษี บรรจุภัณฑ์ และสื่อโฆษณา",
  "ทำสัญญาโอนลิขสิทธิ์เป็นหนังสือลงลายมือชื่อทุกครั้งที่จ้างออกแบบโลโก้หรือพัฒนาซอฟต์แวร์",
  "ตั้งระบบเตือนกำหนดเวลาสำคัญ ได้แก่ อุทธรณ์และคัดค้านหกสิบวัน คำขอตรวจสอบการประดิษฐ์ห้าปี และการต่ออายุ",
  "ผู้ขอที่อยู่ต่างประเทศควรกำหนดตัวแทนหรือสถานที่ติดต่อในไทยไว้ล่วงหน้า เพื่อรับหนังสือแจ้งของนายทะเบียนทันกำหนด",
  "จัดชุดเอกสารบริษัทและหนังสือมอบอำนาจให้สะกดชื่อตรงกันทุกฉบับ ก่อนส่งแปลและรับรองคำแปล",
];

const CAUTIONS_TH = [
  "เข้าใจว่าจดโดเมนหรือจดชื่อนิติบุคคลแล้วเท่ากับได้สิทธิในเครื่องหมายการค้า",
  "เผยแพร่ผลงานหรือออกบูธก่อนยื่นคำขอสิทธิบัตร ทำให้ขาดความใหม่",
  "ปล่อยกำหนดหกสิบวันในการอุทธรณ์คำสั่งนายทะเบียนจนคำสั่งเป็นที่สุด",
  "ไม่ยื่นคำขอให้ตรวจสอบการประดิษฐ์ภายในห้าปีนับแต่วันประกาศโฆษณา ทำให้คำขอถือว่าละทิ้ง",
  "เชื่อว่าการแจ้งข้อมูลลิขสิทธิ์คือการจดทะเบียนที่ยืนยันความเป็นเจ้าของโดยเด็ดขาด",
  "ให้ผู้รับจ้างเก็บไฟล์ต้นฉบับงานออกแบบไว้โดยไม่มีสัญญาโอนสิทธิเป็นหนังสือ",
  "แชร์ข้อมูลสูตรหรือฐานลูกค้าให้คู่ค้าโดยไม่มีสัญญารักษาความลับและไม่มีมาตรการจำกัดการเข้าถึง",
  "ปล่อยอนุสิทธิบัตรหรือทะเบียนเครื่องหมายขาดอายุ ทำให้สิทธิสิ้นไปและฟื้นคืนไม่ได้",
];

export const Route = createFileRoute("/ip/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/ip-corpus");
    return { faqs: m.IP_FAQ_TH };
  },
  component: IpFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `ทรัพย์สินทางปัญญาสำหรับธุรกิจและชาวต่างชาติ — FAQ ${faqs.length} ข้อ`,
        description:
          "จดทะเบียนเครื่องหมายการค้า ยื่นระหว่างประเทศผ่านพิธีสารมาดริด คัดค้านและอุทธรณ์ ลิขสิทธิ์ สิทธิบัตรและอนุสิทธิบัตร ความลับทางการค้า สิ่งบ่งชี้ทางภูมิศาสตร์ และหนังสือมอบอำนาจพร้อมคำแปลรับรอง สอบถามทีมงานได้ทุกช่องทาง",
        path: "/ip/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/ip/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ ทรัพย์สินทางปัญญา", url: `${site}/ip/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/ip/faq")) },
      ],
    };
  },
});

function IpFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>FAQ ทรัพย์สินทางปัญญา</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          ทรัพย์สินทางปัญญาในประเทศไทย: เครื่องหมายการค้า ลิขสิทธิ์ สิทธิบัตร ความลับทางการค้า และเอกสารมอบอำนาจ
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          รวม {faqs.length} คำตอบอ้างอิงกรมทรัพย์สินทางปัญญา (พ.ร.บ.เครื่องหมายการค้า พ.ศ. 2534,
          พ.ร.บ.ลิขสิทธิ์ พ.ศ. 2537, พ.ร.บ.สิทธิบัตร พ.ศ. 2522, พ.ร.บ.ความลับทางการค้า พ.ศ. 2545
          และ พ.ร.บ.คุ้มครองสิ่งบ่งชี้ทางภูมิศาสตร์ พ.ศ. 2546) พร้อมพิธีสารมาดริดและสนธิสัญญา PCT
          ข้อมูลโดยประมาณ ณ สิงหาคม 2569 โปรดยืนยันกับหน่วยงานก่อนยื่น
        </p>

        <section aria-label="หมวดคำถาม" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">เลือกอ่านตามหมวด</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {IP_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/ip/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleTh}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descTh}</p>
              </li>
            ))}
          </ul>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_TH} locale="th" />
        <ServiceCautionBlock items={CAUTIONS_TH} locale="th" />

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ให้ทีมงานตรวจชุดเอกสารทรัพย์สินทางปัญญาก่อนยื่น</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งประเภทงาน ประเทศของผู้ขอ และกำหนดเวลาที่ต้องยื่น สอบถามได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อทีมงาน</a>
        </section>
        <FaqSourceNote topic="ip" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}
